import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { PERMISSIONS } from '@optiflow/types';
import { navigationRoutes } from '@/components/layout/navigation';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MainLayout } from '@/layouts/MainLayout';
import { LoginPage } from '@/modules/auth/pages/LoginPage';
import { BranchesPage } from '@/modules/branches/pages/BranchesPage';
import { CustomerProfilePage } from '@/modules/customers/pages/CustomerProfilePage';
import { CustomersPage } from '@/modules/customers/pages/CustomersPage';
import { frameRecommendationRoute } from '@/modules/frame-recommendations/routes';
import { EyeExaminationsEntry } from '@/modules/medical/pages/EyeExaminationsEntry';
import { eyeExaminationRoutes } from '@/modules/medical/routes';
import { OpticalJobsQueuePage } from '@/modules/optical-orders/pages/OpticalJobsQueuePage';
import { OpticalLabsPage } from '@/modules/optical-orders/pages/OpticalLabsPage';
import { labTicketRoute, opticalOrderRoutes } from '@/modules/optical-orders/routes';
import { LabelBuilderPage } from '@/modules/catalog/pages/LabelBuilderPage';
import { LabelPrintPage } from '@/modules/catalog/pages/LabelPrintPage';
import { ProductDetailPage } from '@/modules/catalog/pages/ProductDetailPage';
import { ProductsPage } from '@/modules/catalog/pages/ProductsPage';
import { GoodsReceiptDetailPage } from '@/modules/purchasing/pages/GoodsReceiptDetailPage';
import { GoodsReceiptsPage } from '@/modules/purchasing/pages/GoodsReceiptsPage';
import { PurchaseOrderDetailPage } from '@/modules/purchasing/pages/PurchaseOrderDetailPage';
import { PurchaseOrderEditorPage } from '@/modules/purchasing/pages/PurchaseOrderEditorPage';
import { PurchaseOrdersPage } from '@/modules/purchasing/pages/PurchaseOrdersPage';
import { ReceiveGoodsPage } from '@/modules/purchasing/pages/ReceiveGoodsPage';
import { RecordSupplierInvoicePage } from '@/modules/purchasing/pages/RecordSupplierInvoicePage';
import { SupplierInvoiceDetailPage } from '@/modules/purchasing/pages/SupplierInvoiceDetailPage';
import { SupplierInvoicesPage } from '@/modules/purchasing/pages/SupplierInvoicesPage';
import { SupplierDetailPage } from '@/modules/purchasing/pages/SupplierDetailPage';
import { SuppliersPage } from '@/modules/purchasing/pages/SuppliersPage';
import { PayablesDashboardPage } from '@/modules/payables/pages/PayablesDashboardPage';
import { ReportsOverviewPage } from '@/modules/reports/pages/ReportsOverviewPage';
import { CrmReportPage } from '@/modules/reports/pages/CrmReportPage';
import { CustomersReportPage } from '@/modules/reports/pages/CustomersReportPage';
import { FinanceReportPage } from '@/modules/reports/pages/FinanceReportPage';
import { OpticalReportPage } from '@/modules/reports/pages/OpticalReportPage';
import { InventoryReportPage } from '@/modules/reports/pages/InventoryReportPage';
import { SalesReportPage } from '@/modules/reports/pages/SalesReportPage';
import { SmartReportPage } from '@/modules/reports/pages/SmartReportPage';
import { ReportingSettingsPage } from '@/modules/reports/pages/ReportingSettingsPage';
import { PrintSettingsPage } from '@/modules/sales/print-settings/PrintSettingsPage';
import { CompanyPage } from '@/modules/company/CompanyPage';
import { payablesRoutes } from '@/modules/payables/routes';
import { ExpensesPage } from '@/modules/expenses/pages/ExpensesPage';
import { expenseRoutes } from '@/modules/expenses/routes';
import { AutomationsPage } from '@/modules/crm/pages/AutomationsPage';
import { CommunicationsPage } from '@/modules/crm/pages/CommunicationsPage';
import { TemplatesPage } from '@/modules/crm/pages/TemplatesPage';
import { WhatsAppSettingsPage } from '@/modules/whatsapp/pages/WhatsAppSettingsPage';
import { ProductCategoriesPage } from '@/modules/inventory/pages/ProductCategoriesPage';
import { StockAlertsPage } from '@/modules/inventory/pages/StockAlertsPage';
import { StockPage } from '@/modules/inventory/pages/StockPage';
import { CounterSalePage } from '@/modules/sales/pages/CounterSalePage';
import { OpticalOrderPage } from '@/modules/sales/pages/OpticalOrderPage';
import { InvoicesPage } from '@/modules/sales/pages/InvoicesPage';
import { InvoicePage } from '@/modules/sales/pages/InvoicePage';
import { PaymentPlanPage } from '@/modules/sales/pages/PaymentPlanPage';
import { ReceivablesPage } from '@/modules/sales/pages/ReceivablesPage';
import { PrintPage } from '@/modules/sales/print/PrintPage';
import { VerifyDocumentPage } from '@/modules/sales/print/VerifyDocumentPage';
import { ReturnDetailPage } from '@/modules/sales/pages/ReturnDetailPage';
import { ReturnsPage } from '@/modules/sales/pages/ReturnsPage';
import { SalesOrdersPage } from '@/modules/sales/pages/SalesOrdersPage';
import { StocktakePage } from '@/modules/inventory/pages/StocktakePage';
import { StocktakeSheetPage } from '@/modules/inventory/pages/StocktakeSheetPage';
import { TransfersPage } from '@/modules/inventory/pages/TransfersPage';
import { DashboardPage } from '@/modules/dashboard/pages/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';
import { ProtectedRoute } from './ProtectedRoute';
import { RequirePermission } from './RequirePermission';

// Loaded only when a phone opens the page, so the staff app never carries the capture code.
const CapturePage = lazy(() =>
  import('@/modules/capture/CapturePage').then((module) => ({ default: module.CapturePage })),
);

/**
 * Pages that exist for real. Everything else in the navigation tree renders a placeholder.
 *
 * Keeping this as an override map means the router and the sidebar are generated from one source,
 * so a new module cannot appear in the menu without a route or ship a route nothing links to.
 */
const IMPLEMENTED_PAGES: Record<string, ReactElement> = {
  '/': <DashboardPage />,
  '/customers': <CustomersPage />,
  '/settings/whatsapp': <WhatsAppSettingsPage />,
  '/crm/notifications': <CommunicationsPage />,
  '/crm/automations': <AutomationsPage />,
  '/crm/templates': <TemplatesPage />,
  '/customers/examinations': <EyeExaminationsEntry />,
  '/inventory/products': <ProductsPage />,
  '/inventory/categories': <ProductCategoriesPage />,
  '/inventory/stock': <StockPage />,
  '/inventory/transfers': <TransfersPage />,
  '/inventory/stocktake': <StocktakePage />,
  '/sales/counter': <CounterSalePage />,
  '/sales/orders': <SalesOrdersPage />,
  '/sales/optical-jobs': <OpticalJobsQueuePage />,
  '/sales/returns': <ReturnsPage />,
  '/sales/invoices': <InvoicesPage />,
  '/sales/receivables': <ReceivablesPage />,
  '/inventory/alerts': <StockAlertsPage />,
  '/purchasing/orders': <PurchaseOrdersPage />,
  '/purchasing/receipts': <GoodsReceiptsPage />,
  '/purchasing/invoices': <SupplierInvoicesPage />,
  '/purchasing/suppliers': <SuppliersPage />,
  '/finance/payables': <PayablesDashboardPage />,
  '/finance/expenses': <ExpensesPage />,
  '/settings/branches': <BranchesPage />,
  '/settings/optical-labs': <OpticalLabsPage />,
  '/reports': <ReportsOverviewPage />,
  '/settings/reporting': <ReportingSettingsPage />,
  '/settings/printing': <PrintSettingsPage />,
  '/settings/company': <CompanyPage />,
};

function buildModuleRoutes(): RouteObject[] {
  return navigationRoutes().map((leaf) => {
    const element = IMPLEMENTED_PAGES[leaf.to] ?? <PlaceholderPage titleKey={leaf.labelKey} />;

    // A route without a declared permission is reachable by any authenticated user.
    if (!leaf.permission) return { path: leaf.to, element };

    return {
      element: <RequirePermission permission={leaf.permission} />,
      children: [{ path: leaf.to, element }],
    };
  });
}

/**
 * Route table.
 *
 * Authentication is applied once at the tree root rather than page by page, so a new page is
 * protected by default — the same posture as the API's global JwtAuthGuard.
 */
export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [{ path: '/login', element: <LoginPage /> }],
  },
  // Outside MainLayout on purpose: a printed document is not a screen with the sidebar removed.
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/print/receipt/:orderId', element: <PrintPage kind="receipt" /> },
      { path: '/print/invoice/:invoiceId', element: <PrintPage kind="invoice" /> },
      labTicketRoute,
      {
        element: <RequirePermission permission={PERMISSIONS.CATALOG_VIEW} />,
        children: [{ path: '/print/labels', element: <LabelPrintPage /> }],
      },
    ],
  },

  // Public: whoever scans the QR on a receipt has no account.
  { path: '/verify/:token', element: <VerifyDocumentPage /> },

  // Public: a phone holding the desk's one-time code has no account either — only its capture capability.
  {
    path: '/m',
    element: (
      <Suspense fallback={null}>
        <CapturePage />
      </Suspense>
    ),
  },

  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <MainLayout />,
        children: [
          ...buildModuleRoutes(),

          // Detail routes are not navigation entries; React Router ranks the static `/customers/examinations` above this dynamic segment, so the two never collide.
          {
            element: <RequirePermission permission={PERMISSIONS.CUSTOMERS_VIEW} />,
            children: [{ path: '/customers/:customerId', element: <CustomerProfilePage /> }],
          },
          // Report tabs beyond the Overview are not sidebar entries, so they are routed here; the page itself also requires sales.view.
          {
            element: <RequirePermission permission={PERMISSIONS.REPORTING_VIEW} />,
            children: [
              { path: '/reports/sales', element: <SalesReportPage /> },
              { path: '/reports/inventory', element: <InventoryReportPage /> },
              { path: '/reports/finance', element: <FinanceReportPage /> },
              { path: '/reports/customers', element: <CustomersReportPage /> },
              { path: '/reports/optical', element: <OpticalReportPage /> },
              { path: '/reports/crm', element: <CrmReportPage /> },
              { path: '/reports/smart', element: <SmartReportPage /> },
            ],
          },
          frameRecommendationRoute,
          eyeExaminationRoutes,
          ...opticalOrderRoutes,
          ...payablesRoutes,
          ...expenseRoutes,
          {
            element: <RequirePermission permission={PERMISSIONS.CATALOG_VIEW} />,
            children: [
              { path: '/inventory/products/:productId', element: <ProductDetailPage /> },
              { path: '/inventory/labels', element: <LabelBuilderPage /> },
            ],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.INVENTORY_VIEW} />,
            children: [
              { path: '/inventory/stocktake/:sessionId', element: <StocktakeSheetPage /> },
            ],
          },

          // Purchasing's detail routes. `new` is listed before the dynamic segment so it is never read as an id.
          {
            element: <RequirePermission permission={PERMISSIONS.SUPPLIERS_VIEW} />,
            children: [
              { path: '/purchasing/suppliers/:supplierId', element: <SupplierDetailPage /> },
            ],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.PURCHASING_CREATE} />,
            children: [{ path: '/purchasing/orders/new', element: <PurchaseOrderEditorPage /> }],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.PURCHASING_UPDATE} />,
            children: [
              { path: '/purchasing/orders/:orderId/edit', element: <PurchaseOrderEditorPage /> },
            ],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.PURCHASING_RECEIVE} />,
            children: [
              { path: '/purchasing/orders/:orderId/receive', element: <ReceiveGoodsPage /> },
            ],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.PURCHASING_INVOICE} />,
            children: [
              { path: '/purchasing/invoices/new', element: <RecordSupplierInvoicePage /> },
            ],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.PURCHASING_VIEW} />,
            children: [
              { path: '/purchasing/orders/:orderId', element: <PurchaseOrderDetailPage /> },
              { path: '/purchasing/receipts/:receiptId', element: <GoodsReceiptDetailPage /> },
              {
                path: '/purchasing/invoices/:invoiceId',
                element: <SupplierInvoiceDetailPage />,
              },
            ],
          },
          {
            element: <RequirePermission permission={PERMISSIONS.SALES_VIEW} />,
            children: [
              { path: '/sales/invoices/:invoiceId', element: <InvoicePage /> },
              { path: '/sales/orders/:orderId', element: <OpticalOrderPage /> },
              { path: '/sales/optical/:orderId', element: <OpticalOrderPage /> },
              { path: '/sales/returns/:returnId', element: <ReturnDetailPage /> },
              { path: '/sales/plans/:planId', element: <PaymentPlanPage /> },
            ],
          },

          // Branches moved under Settings; the old path is kept so existing links survive.
          { path: '/branches', element: <Navigate to="/settings/branches" replace /> },
          // The order-based collection list gave way to the optical job queue; old links land there.
          { path: '/sales/collection', element: <Navigate to="/sales/optical-jobs" replace /> },

          { path: '/unauthorized', element: <UnauthorizedPage /> },
          { path: '/404', element: <NotFoundPage /> },
          { path: '*', element: <Navigate to="/404" replace /> },
        ],
      },
    ],
  },
]);
